import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import {Typography} from '@nur-portal/ui-kit';

import {CommentsEventLogger} from '../../../../services/analytics/comments';
import {USER_ROLES} from '../../../constants/user';
import {useUser} from '../../../hooks/use-user';
// Components
import {Button} from '../../button';
import CommentAlert from '../comment-alert/comment-alert';

import CommentInput from './input/comment-input';

// Styles
import styles from './comment-form-area.module.css';

const FORM_TYPES = {
  main: 'main',
  child: 'child'
};

const CommentForm = ({isLoading, error, onSubmit, onReset, type, articleId, isLast}) => {
  const {t} = useTranslation();
  const [commentText, setCommentText] = useState('');
  const [inputHasUpdated, setInputHasUpdated] = useState(false);
  const [isFormLarge, setIsFormLarge] = useState(false);
  const isSaveButtonDisabled = !commentText || isLoading;

  const callbackRef = useRef();

  const user = useUser();

  const hasPhoneNumber = user.role === USER_ROLES.EXTENDED;
  const hasNickname = Boolean(user.username);

  if (!hasNickname || !hasPhoneNumber) {
    return null;
  }

  const formClasses = clsx({
    [styles.formWithMargin]: isFormLarge
  });

  const handleClickCancelButton = () => {
    type === FORM_TYPES.main
      ? CommentsEventLogger.clickCancelButtonOnMainComment(articleId)
      : CommentsEventLogger.clickCancelButtonOnChildComment(articleId);
    setCommentText('');
    onReset();
  };
  const handleClickSaveButton = () => {
    if (!commentText) {
      return;
    }
    type === FORM_TYPES.main
      ? CommentsEventLogger.publishMainComment(articleId)
      : CommentsEventLogger.publishChildCommentReply(articleId);
    onSubmit(commentText);
  };

  const handleInputChange = text => {
    if (!inputHasUpdated) {
      type === FORM_TYPES.main
        ? CommentsEventLogger.fillMainComment(articleId)
        : CommentsEventLogger.fillChildComment(articleId);
      setInputHasUpdated(true);
    }

    setCommentText(text);
  };

  const onFocusInput = callbackForRef => {
    if (isLast) {
      setIsFormLarge(true);
      callbackRef.current = callbackForRef;
    } else {
      callbackForRef && callbackForRef();
    }
  };

  useEffect(() => {
    if (isFormLarge && typeof callbackRef.current === 'function') {
      callbackRef.current();
    }
  }, [isFormLarge, callbackRef]);

  return (
    <form
      onSubmit={e => e.preventDefault()}
      className={formClasses}
    >
      <CommentInput
        value={commentText}
        onInput={handleInputChange}
        onFocus={onFocusInput}
        onBlur={() => setIsFormLarge(false)}
      />

      <div className={styles.buttonsWrapper}>
        {/*  Cancel button*/}
        <Button
          onClick={handleClickCancelButton}
          variant="border"
        >
          {t('articleComments.cancel')}
        </Button>
        {/*  Confirm button*/}
        <Button
          onClick={handleClickSaveButton}
          variant={'primary'}
          className={styles.primaryButton}
          disabled={isSaveButtonDisabled}
        >
          {t('articleComments.publish')}
        </Button>
      </div>

      {Boolean(error) && (
        <CommentAlert
          type='error'
          className={styles.alert}
        >
          <Typography variant='text-14'>
            {error}
          </Typography>
        </CommentAlert>
      )}
    </form>
  );
};

CommentForm.propTypes = {
  error: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  articleId: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(FORM_TYPES)),
  isLast: PropTypes.bool,
  isLoading: PropTypes.bool
};

CommentForm.defaultProps = {
  error: '',
  type: FORM_TYPES.child,
  isLast: false,
  isLoading: false
};

export default CommentForm;
